import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FolderKanban, ShieldCheck, Newspaper, Megaphone, Briefcase,
  FileUser, MessageSquareQuote, IndianRupee, HelpCircle, Users, CalendarDays,
  Inbox, Settings, UserCog, LogOut, Rocket, RocketIcon, ClipboardList, UserCog2, ShoppingCart, BarChart3,
} from "lucide-react";
import { StatsDashboard } from "@/components/admin/StatsDashboard";
import { ProjectsModule } from "@/components/admin/ProjectsModule";
import { CrudModule } from "@/components/admin/CrudModule";
import { EnquiriesModule } from "@/components/admin/EnquiriesModule";
import { ApplicationsModule } from "@/components/admin/ApplicationsModule";
import { SettingsModule } from "@/components/admin/SettingsModule";
import { AdminsModule } from "@/components/admin/AdminsModule";
import { ServicesModule } from "@/components/admin/portal/ServicesModule";
import { SpecialistsModule } from "@/components/admin/portal/SpecialistsModule";
import { PortalStartupsModule } from "@/components/admin/portal/PortalStartupsModule";
import { PortalOrdersModule } from "@/components/admin/portal/PortalOrdersModule";
import { PortalAnalyticsModule } from "@/components/admin/portal/PortalAnalyticsModule";
import { MODULES } from "@/components/admin/moduleConfigs";

const SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "portal-startups", label: "Startup Projects", icon: RocketIcon },
  { id: "portal-orders", label: "Service Orders", icon: ShoppingCart },
  { id: "portal-services", label: "Services Master", icon: ClipboardList },
  { id: "portal-specialists", label: "Specialists", icon: UserCog2 },
  { id: "portal-analytics", label: "Portal Analytics", icon: BarChart3 },
  { id: "projects", label: "Projects", icon: FolderKanban },
  { id: "regcategories", label: "Registrations", icon: ShieldCheck },
  { id: "posts", label: "Blog", icon: Newspaper },
  { id: "news", label: "News", icon: Megaphone },
  { id: "jobs", label: "Careers", icon: Briefcase },
  { id: "applications", label: "Applications", icon: FileUser },
  { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { id: "pricing", label: "Pricing", icon: IndianRupee },
  { id: "faqs", label: "FAQ", icon: HelpCircle },
  { id: "team", label: "Team", icon: Users },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "settings", label: "Settings", icon: Settings },
  { id: "admins", label: "Admin Users", icon: UserCog },
];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");

  const renderModule = () => {
    switch (tab) {
      case "dashboard":
        return <StatsDashboard onNavigate={setTab} />;
      case "projects":
        return <ProjectsModule />;
      case "portal-startups":
        return <PortalStartupsModule />;
      case "portal-orders":
        return <PortalOrdersModule />;
      case "portal-services":
        return <ServicesModule />;
      case "portal-specialists":
        return <SpecialistsModule />;
      case "portal-analytics":
        return <PortalAnalyticsModule />;
      case "applications":
        return <ApplicationsModule />;
      case "enquiries":
        return <EnquiriesModule />;
      case "settings":
        return <SettingsModule />;
      case "admins":
        return <AdminsModule />;
      default:
        return <CrudModule key={tab} config={MODULES[tab]} />;
    }
  };

  return (
    <div className="min-h-screen bg-grid-tech" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF8800] to-[#ff5e00] flex items-center justify-center">
              <Rocket className="w-4 h-4 text-[#06080E]" strokeWidth={2.4} />
            </div>
            <div>
              <p className="font-display font-bold text-white leading-none">LAUNCHLAB ADMIN</p>
              <p className="text-[10px] font-mono-tech tracking-widest text-slate-500 uppercase mt-0.5">{admin?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site-link" className="text-xs text-slate-400 hover:text-white transition-colors hidden sm:block">View Website</a>
            <Button size="sm" variant="outline" data-testid="admin-logout-button" onClick={logout} className="border-white/15 text-slate-300 hover:bg-white/5">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 border-r border-white/10 min-h-[calc(100vh-4rem)] py-6 px-3 hidden lg:block sticky top-16 self-start" data-testid="admin-sidebar">
          <nav className="space-y-1">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`admin-tab-${id}`}
                onClick={() => setTab(id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  tab === id
                    ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" /> {label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Mobile tab bar */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-white/10" data-testid="admin-mobile-tabs">
          <div className="flex overflow-x-auto gap-1 px-3 py-2.5">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`admin-tab-mobile-${id}`}
                onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  tab === id ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "text-slate-400"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <main className="flex-1 min-w-0 px-4 sm:px-8 py-8 pb-24 lg:pb-8" data-testid="admin-content">
          {renderModule()}
        </main>
      </div>
    </div>
  );
}
